/**
 * Painéis do usuário: meus painéis, painéis da organização e aprovação do supervisor (US-407 · GUS-802/805) — Nexora Grid.
 *
 * Criado em: 17/10/2026 · Última modificação: 18/10/2026 · Versão: 1.1.0
 *
 * ADR-0008: o painel pertence a quem o criou e fica na pasta pessoal dele no Grafana até ele pedir para compartilhar.
 * - **Meus painéis**: rascunho → (ver ao vivo) aprovar → pessoal → compartilhar (fila do supervisor) → publicado;
 *   descartar, excluir, cancelar o pedido e despublicar.
 * - **Da organização**: os publicados da operadora, para todos dela.
 * - **Aprovações**: supervisor ou coordenador da operadora aprova ou rejeita (com comentário); nunca o próprio dono.
 * - **Novo painel**: construtor sem SQL (medida, equipamentos, tipo, agregação). A consulta gerada é validada no
 *   backend (só leitura, só `telemetria`/`alarmes`). Os painéis abrem embutidos com o login único (US-408).
 * D-010: nomes das áreas pela fonte única `nomes.ts` (o nome do menu é o padrão do sistema).
 */
import { NOMES } from "../nomes";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ErroApi } from "../api";
import type { Ativo } from "../cadastro/tipos";
import { urlPainel, useTokenPainel } from "../cco/PainelGrafana";
import { MEDIDAS } from "../cco/medidas";
import { pode } from "../permissoes";
import { useSessao } from "../sessao";

type Painel = {
  id: string; empresa: string; dono: string; uid_grafana: string; titulo: string; estado: string; origem: string; comentario: string | null;
  criado_em: string; atualizado_em: string; especificacao: { titulo: string; descricao: string; intervalo: string; paineis: unknown[] };
};
type Aba = "meus" | "organizacao" | "aprovacoes" | "novo";

const ESTADO: Record<string, string> = { rascunho: "rascunho", pessoal: "pessoal", aguardando_aprovacao: "aguardando aprovação",
  publicado: "publicado na organização", rejeitado: "rejeitado" };

export function Paineis() {
  const { usuario, empresa } = useSessao();
  const [aba, setAba] = useState<Aba>("meus");
  const [meus, setMeus] = useState<Painel[]>([]);
  const [org, setOrg] = useState<Painel[]>([]);
  const [fila, setFila] = useState<Painel[]>([]);
  const [aberto, setAberto] = useState<Painel | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const aprova = usuario ? pode.aprovarPaineis(usuario) : false;

  const carregar = useCallback(() => {
    if (!empresa) return;
    api<Painel[]>(`/v1/paineis?empresa=${empresa}`).then(setMeus).catch(() => setMeus([]));
    api<Painel[]>(`/v1/paineis/organizacao?empresa=${empresa}`).then(setOrg).catch(() => setOrg([]));
    if (aprova) api<Painel[]>(`/v1/paineis/aprovacoes?empresa=${empresa}`).then(setFila).catch(() => setFila([]));
  }, [empresa, aprova]);
  useEffect(() => { carregar(); }, [carregar]);

  const agir = async (p: Painel, acao: string, corpo?: unknown, metodo?: string) => {
    try {
      const r = await api<Painel>(acao ? `/v1/paineis/${p.id}/${acao}` : `/v1/paineis/${p.id}`, { metodo: metodo ?? "POST", corpo });
      setAviso(`"${p.titulo}": ${ESTADO[r.estado] ?? r.estado}`);
      setAberto((a) => (a?.id === p.id ? (r.estado === "removido" ? null : { ...p, ...r }) : a));
      carregar();
    } catch (e) {
      setAviso(e instanceof ErroApi ? `Não foi possível: ${e.message}` : "Falha de rede.");
    }
  };

  if (!usuario) return null;
  if (!empresa) return <section className="tela-paineis"><p className="nx-suave">Escolha uma operadora para ver os painéis.</p></section>;
  return (
    <section className="tela-paineis" aria-label="Painéis">
      <header className="cabecalho-paineis">
        <h2 className="nx-titulo">{NOMES.paineis}</h2>
        <nav className="sub-abas" aria-label="Painéis">
          <button type="button" className={aba === "meus" ? "ativa" : ""} onClick={() => setAba("meus")}>Meus painéis ({meus.length})</button>
          <button type="button" className={aba === "organizacao" ? "ativa" : ""} onClick={() => setAba("organizacao")}>Da organização ({org.length})</button>
          {aprova && <button type="button" className={aba === "aprovacoes" ? "ativa" : ""} onClick={() => setAba("aprovacoes")}>Aprovações ({fila.length})</button>}
          <button type="button" className={`nx-botao nx-botao-principal${aba === "novo" ? " ativa" : ""}`} onClick={() => setAba("novo")}>+ Novo painel</button>
        </nav>
      </header>
      {aviso && <p className="aviso-paineis" role="status">{aviso} <button type="button" className="nx-botao" onClick={() => setAviso(null)}>ok</button></p>}
      {aba === "novo" && <NovoPainel empresa={empresa} aoCriar={(p) => { setAviso(`Rascunho "${p.titulo}" criado: confira ao vivo e aprove.`); setAba("meus"); setAberto(p); carregar(); }} />}
      {aba !== "novo" && (
        <div className="grade-paineis-usuario">
          <ul className="lista-paineis">
            {(aba === "meus" ? meus : aba === "organizacao" ? org : fila).map((p) => (
              <li key={p.id} className={`item-painel${aberto?.id === p.id ? " aberto" : ""}`}>
                <button type="button" className="abrir-painel" onClick={() => setAberto(p)}>
                  <b>{p.titulo}</b>
                  <small className="nx-suave">{p.especificacao.paineis.length} gráfico(s) · {p.dono}{p.origem === "recorrencia" ? " · sugerido pelo assistente" : ""}</small>
                </button>
                <span className={`nx-selo estado-${p.estado}`}>{ESTADO[p.estado] ?? p.estado}</span>
              </li>
            ))}
            {!(aba === "meus" ? meus : aba === "organizacao" ? org : fila).length && (
              <li className="nx-suave vazio">{aba === "meus" ? "Você ainda não tem painéis. Crie um em + Novo painel."
                : aba === "organizacao" ? "Nenhum painel publicado na operadora." : "Nenhum pedido aguardando aprovação."}</li>
            )}
          </ul>
          <div className="detalhe-painel">
            {aberto ? <DetalhePainel p={aberto} login={usuario.login} aprova={aprova} agir={agir} />
              : <p className="nx-suave">Selecione um painel para ver ao vivo.</p>}
          </div>
        </div>
      )}
    </section>
  );
}

function DetalhePainel({ p, login, aprova, agir }: { p: Painel; login: string; aprova: boolean; agir: (p: Painel, acao: string, corpo?: unknown, metodo?: string) => void }) {
  const { dados, erro } = useTokenPainel();
  const [comentario, setComentario] = useState("");
  const [recarga, setRecarga] = useState(0);
  const dono = p.dono === login;
  // o Grafana guarda as permissões de pasta recém-criada por 30–60 s: enquanto o painel é novo, a prévia recarrega sozinha
  const idade = Date.now() - Date.parse(p.atualizado_em);
  useEffect(() => {
    if (idade > 90000) return;
    const relogio = window.setInterval(() => setRecarga((n) => n + 1), 15000);
    const fim = window.setTimeout(() => window.clearInterval(relogio), Math.max(0, 90000 - idade));
    return () => { window.clearInterval(relogio); window.clearTimeout(fim); };
  }, [p.id, p.uid_grafana, idade > 90000]); // eslint-disable-line react-hooks/exhaustive-deps
  const src = dados ? urlPainel(dados, p.empresa, p.uid_grafana, { de: `now-${p.especificacao.intervalo}` }) : null;
  return (
    <div className="nx-cartao">
      <h3>{p.titulo} <span className={`nx-selo estado-${p.estado}`}>{ESTADO[p.estado] ?? p.estado}</span></h3>
      {p.especificacao.descricao && <p className="nx-suave">{p.especificacao.descricao}</p>}
      {p.comentario && <p className="comentario-painel">Comentário do supervisor: “{p.comentario}”</p>}
      <div className="acoes-painel">
        {dono && p.estado === "rascunho" && <>
          <button type="button" className="nx-botao nx-botao-principal" onClick={() => agir(p, "aprovar")}>Aprovar rascunho</button>
          <button type="button" className="nx-botao" onClick={() => agir(p, "", undefined, "DELETE")}>Descartar</button></>}
        {dono && (p.estado === "pessoal" || p.estado === "rejeitado") && <>
          <button type="button" className="nx-botao nx-botao-principal" onClick={() => agir(p, "compartilhar")}>Compartilhar com a organização</button>
          <button type="button" className="nx-botao" onClick={() => agir(p, "", undefined, "DELETE")}>Excluir</button></>}
        {dono && p.estado === "aguardando_aprovacao" && <button type="button" className="nx-botao" onClick={() => agir(p, "cancelar")}>Cancelar pedido</button>}
        {dono && p.estado === "publicado" && <button type="button" className="nx-botao" onClick={() => agir(p, "despublicar")}>Tirar da organização</button>}
        {!dono && aprova && (p.estado === "aguardando_aprovacao" || p.estado === "publicado") && (
          <span className="decisao-painel">
            <input value={comentario} onChange={(e) => setComentario(e.target.value)} maxLength={500} aria-label="Comentário da decisão"
              placeholder={p.estado === "publicado" ? "motivo para retirar" : "comentário (obrigatório para rejeitar)"} />
            {p.estado === "aguardando_aprovacao" ? <>
              <button type="button" className="nx-botao nx-botao-principal" onClick={() => agir(p, "decisao", { aprovar: true, comentario })}>Aprovar e publicar</button>
              <button type="button" className="nx-botao" disabled={comentario.trim().length < 3} onClick={() => agir(p, "decisao", { aprovar: false, comentario })}>Rejeitar</button></>
              : <button type="button" className="nx-botao" onClick={() => agir(p, "retirar", { aprovar: false, comentario })}>Retirar da organização</button>}
          </span>
        )}
        {src && <a className="nx-botao" href={src} target="_blank" rel="noopener noreferrer">abrir no Grafana ›</a>}
      </div>
      {erro && <p className="nx-suave">Painéis indisponíveis: {erro}</p>}
      {src && idade <= 90000 && <p className="nx-suave">Painel recém-criado: o Grafana pode levar até 1 minuto para liberar o acesso; a prévia recarrega sozinha.</p>}
      {src && <iframe key={`${p.uid_grafana}-${recarga}`} className="painel-grafana painel-usuario" title={`Painel: ${p.titulo}`} src={`${src}&kiosk`} />}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ construtor
type Grafico = { titulo: string; tipo: "serie_temporal" | "valor" | "medidor" | "barras"; medida: string; dispositivos: string[]; agregacao: "avg" | "max" | "min" | "ultimo" };
const MEDIDAS_CONSTRUTOR = ["nivel_reservatorio_pct", "nivel_m", "vazao_instantanea_m3h", "vazao_saida_m3h", "pressao_rede_mca", "pressao_jusante_mca",
  "potencia_ativa_kw", "corrente_fase_a", "tensao_fase_v", "temperatura_bomba_c", "frequencia_inversor_hz", "alarmes"];
const DEVICE = /^[A-Za-z0-9._:-]{1,80}$/;

/** Consulta gerada pelo construtor (o backend valida: só leitura, só telemetria/alarmes). */
export function consultaDe(g: Grafico): string {
  const filtro = g.dispositivos.filter((d) => DEVICE.test(d)).map((d) => `'${d}'`);
  const onde = `$__timeFilter("timestamp")${filtro.length ? ` AND device_id IN (${filtro.join(", ")})` : ""}`;
  if (g.medida === "alarmes") {
    return g.tipo === "serie_temporal"
      ? `SELECT $__timeGroupAlias("timestamp", '1h'), 'alarmes' AS metric, count(*) AS valor FROM alarmes WHERE ${onde} GROUP BY 1 ORDER BY 1`
      : g.tipo === "barras" ? `SELECT device_id AS dispositivo, count(*) AS alarmes FROM alarmes WHERE ${onde} GROUP BY 1 ORDER BY 2 DESC`
        : `SELECT count(*) AS alarmes FROM alarmes WHERE ${onde}`;
  }
  const medida = `medida = '${g.medida}'`;
  if (g.tipo === "serie_temporal") {
    return `SELECT $__timeGroupAlias("timestamp", $__interval), device_id AS metric, avg(valor) AS valor FROM telemetria WHERE ${onde} AND ${medida} GROUP BY 1, 2 ORDER BY 1`;
  }
  if (g.tipo === "barras") return `SELECT device_id AS dispositivo, ${g.agregacao === "ultimo" ? "avg" : g.agregacao}(valor) AS valor FROM telemetria WHERE ${onde} AND ${medida} GROUP BY 1 ORDER BY 2 DESC`;
  if (g.agregacao === "ultimo") return `SELECT valor FROM telemetria WHERE ${onde} AND ${medida} ORDER BY "timestamp" DESC LIMIT 1`;
  return `SELECT ${g.agregacao}(valor) AS valor FROM telemetria WHERE ${onde} AND ${medida}`;
}

const rotulo = (m: string) => (m === "alarmes" ? "Alarmes (contagem)" : MEDIDAS[m]?.rotulo ?? m);
const novoGrafico = (): Grafico => ({ titulo: "", tipo: "serie_temporal", medida: "nivel_reservatorio_pct", dispositivos: [], agregacao: "avg" });

function NovoPainel({ empresa, aoCriar }: { empresa: string; aoCriar: (p: Painel) => void }) {
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [intervalo, setIntervalo] = useState("24h");
  const [graficos, setGraficos] = useState<Grafico[]>([novoGrafico()]);
  const [ativos, setAtivos] = useState<Ativo[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  useEffect(() => { api<Ativo[]>(`/v1/ativos?empresa=${empresa}`).then((l) => setAtivos(l.filter((a) => a.dados.device_id))).catch(() => setAtivos([])); }, [empresa]);
  const opcoes = useMemo(() => ativos.map((a) => ({ device: a.dados.device_id as string, nome: `${a.dados.identificacao} (${a.dados.tipo})` }))
    .sort((a, b) => a.nome.localeCompare(b.nome)), [ativos]);
  const mudar = (i: number, parcial: Partial<Grafico>) => setGraficos((gs) => gs.map((g, k) => (k === i ? { ...g, ...parcial } : g)));

  const criar = async () => {
    setErro(null);
    setEnviando(true);
    const especificacao = {
      titulo: titulo.trim(), descricao: descricao.trim(), intervalo, atualizacao: "1m",
      paineis: graficos.map((g) => ({ titulo: (g.titulo || rotulo(g.medida)).slice(0, 80), tipo: g.tipo, consulta: consultaDe(g),
        unidade: g.medida === "alarmes" ? null : (MEDIDAS[g.medida]?.unidade || null), largura: g.tipo === "serie_temporal" ? 12 : 6, altura: 8 })),
    };
    try {
      aoCriar(await api<Painel>("/v1/paineis", { corpo: { empresa, especificacao, origem: "pedido" } }));
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : "falha de rede");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="nx-cartao construtor-painel">
      <h3>Novo painel</h3>
      <p className="nx-suave">Escolha o que quer acompanhar. O painel nasce como rascunho na sua pasta pessoal; depois de conferir ao vivo, você aprova e pode pedir para compartilhar com a operadora.</p>
      <div className="campos-painel">
        <label className="nx-campo">Título<input value={titulo} onChange={(e) => setTitulo(e.target.value)} maxLength={80} placeholder="ex.: Reservatórios do setor RAP" /></label>
        <label className="nx-campo">Descrição<input value={descricao} onChange={(e) => setDescricao(e.target.value)} maxLength={300} /></label>
        <label className="nx-campo">Período<select value={intervalo} onChange={(e) => setIntervalo(e.target.value)}>
          {["1h", "6h", "24h", "7d", "30d"].map((i) => <option key={i} value={i}>{i.replace("h", " h").replace("d", " dias")}</option>)}</select></label>
      </div>
      {graficos.map((g, i) => (
        <fieldset key={i} className="grafico-construtor">
          <legend>Gráfico {i + 1}</legend>
          <label className="nx-campo">O que medir<select value={g.medida} onChange={(e) => mudar(i, { medida: e.target.value })} aria-label={`Medida do gráfico ${i + 1}`}>
            {MEDIDAS_CONSTRUTOR.map((m) => <option key={m} value={m}>{rotulo(m)}</option>)}</select></label>
          <label className="nx-campo">Como mostrar<select value={g.tipo} onChange={(e) => mudar(i, { tipo: e.target.value as Grafico["tipo"] })}>
            <option value="serie_temporal">linha no tempo</option><option value="valor">número</option><option value="medidor">medidor</option><option value="barras">barras por equipamento</option></select></label>
          {g.tipo !== "serie_temporal" && g.medida !== "alarmes" && (
            <label className="nx-campo">Cálculo<select value={g.agregacao} onChange={(e) => mudar(i, { agregacao: e.target.value as Grafico["agregacao"] })}>
              <option value="avg">média do período</option><option value="max">máximo</option><option value="min">mínimo</option>{g.tipo !== "barras" && <option value="ultimo">último valor</option>}</select></label>
          )}
          <label className="nx-campo">Título do gráfico<input value={g.titulo} onChange={(e) => mudar(i, { titulo: e.target.value })} maxLength={80} placeholder={rotulo(g.medida)} /></label>
          <label className="nx-campo largo">Equipamentos (vazio = todos)
            <select multiple value={g.dispositivos} size={5} aria-label={`Equipamentos do gráfico ${i + 1}`}
              onChange={(e) => mudar(i, { dispositivos: [...e.target.selectedOptions].map((o) => o.value) })}>
              {opcoes.map((o) => <option key={o.device} value={o.device}>{o.nome}</option>)}
            </select></label>
          {graficos.length > 1 && <button type="button" className="nx-botao" onClick={() => setGraficos((gs) => gs.filter((_, k) => k !== i))}>remover gráfico</button>}
        </fieldset>
      ))}
      <div className="acoes-painel">
        {graficos.length < 12 && <button type="button" className="nx-botao" onClick={() => setGraficos((gs) => [...gs, novoGrafico()])}>+ gráfico</button>}
        <button type="button" className="nx-botao nx-botao-principal" disabled={enviando || titulo.trim().length < 3} onClick={() => void criar()}>
          {enviando ? "criando…" : "Criar rascunho"}</button>
      </div>
      {erro && <p className="erro-painel" role="alert">Não foi possível criar: {erro}</p>}
    </div>
  );
}
